import { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import { useControllers } from "../state/controllers";
import { useProjectStore } from "../state/projectStore";
import { useEditorStore } from "../state/editorStore";
import { useEditor } from "../hooks/useEditor";

// Hosts the alphaTab score. The host div is owned ENTIRELY by alphaTab — React must never render
// children into it (alphaTab does innerHTML=""), so the loading hint + range handles are siblings.
export function ChordChart() {
  const hostRef = useRef<HTMLDivElement>(null);
  const { score } = useControllers();
  const tex = useProjectStore((s) => s.tex);
  const editMode = useEditorStore((s) => s.editMode);

  useEffect(() => {
    if (hostRef.current) score.attach(hostRef.current);
    return () => score.detach();
  }, [score]);

  useEffect(() => {
    if (tex) score.setTex(tex);
  }, [tex, score]);

  return (
    <div className={"chord-chart" + (editMode ? " editing" : "")}>
      {!tex && <div className="chart-loading">악보를 불러오는 중…</div>}
      <div ref={hostRef} className="at-host" />
      <RangeHandles />
    </div>
  );
}

// Draggable selection handles at both ends of the 범위 range (mouse + touch via pointer events).
function RangeHandles() {
  const c = useControllers();
  const ed = useEditor();
  const editMode = useEditorStore((s) => s.editMode);
  const rangeMode = useEditorStore((s) => s.rangeMode);
  const activeMeasure = useEditorStore((s) => s.activeMeasure);
  const rangeAnchor = useEditorStore((s) => s.rangeAnchor);
  const rangeEnd = useEditorStore((s) => s.rangeEnd);
  const tex = useProjectStore((s) => s.tex);
  const [, force] = useReducer((x) => x + 1, 0);
  type Knob = { x: number; top: number; h: number };
  const [geo, setGeo] = useState<{ start: Knob; end: Knob; sameRow: boolean } | null>(null);

  // recompute handle positions after every alphaTab render
  useEffect(() => {
    const prev = c.score.onRendered;
    c.score.onRendered = () => { prev?.(); force(); };
    return () => { c.score.onRendered = prev; };
  }, [c]);

  const active = editMode && rangeMode && activeMeasure >= 0 && rangeAnchor != null && rangeEnd != null;
  const anchorKey = rangeAnchor ? `${rangeAnchor.m}:${rangeAnchor.b}` : "";
  const endKey = rangeEnd ? `${rangeEnd.m}:${rangeEnd.b}` : "";

  useLayoutEffect(() => {
    if (!active) { setGeo(null); return; }
    const a = rangeAnchor!, b = rangeEnd!;
    const [lo, hi] = (a.m < b.m || (a.m === b.m && a.b <= b.b)) ? [a, b] : [b, a];
    const compute = () => {
      const A = c.score.beatBounds(lo.m, lo.b);
      const B = c.score.beatBounds(hi.m, hi.b);
      const host = c.score.hostEl();
      if (!A || !B || !host) { setGeo(null); return; }
      const ox = host.offsetLeft, oy = host.offsetTop;
      setGeo({
        start: { x: ox + A.x, top: oy + A.y, h: A.h },
        end: { x: ox + B.x + B.w, top: oy + B.y, h: B.h },
        sameRow: Math.abs(A.y - B.y) < 2,
      });
    };
    const id = requestAnimationFrame(compute);
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, anchorKey, endKey, tex]);

  if (!active || !geo) return null;

  const onDrag = (side: "start" | "end") => (e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    const move = (ev: PointerEvent) => {
      const hit = c.score.beatAtClient(ev.clientX, ev.clientY);
      if (hit) ed.setRangeBound(side, hit.measureIndex, hit.beatInBar);
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div className="range-overlay">
      {geo.sameRow && (
        <div className="range-band" style={{ left: geo.start.x, top: geo.start.top, width: Math.max(0, geo.end.x - geo.start.x), height: geo.start.h }} />
      )}
      <div className="range-handle" style={{ left: geo.start.x, top: geo.start.top, height: geo.start.h }} onPointerDown={onDrag("start")}>
        <span className="range-knob" />
      </div>
      <div className="range-handle" style={{ left: geo.end.x, top: geo.end.top, height: geo.end.h }} onPointerDown={onDrag("end")}>
        <span className="range-knob" />
      </div>
    </div>
  );
}
